import * as React from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
	GET_WAREHOUSES,
	UPDATE_WAREHOUSE,
	DELETE_WAREHOUSE,
} from "../graphql/warehouses";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import TableHead from "@mui/material/TableHead";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";

import { columns, emptyWarehouse } from "./common";
import DeleteDialog from "./DeleteDialog";
import WarehouseTableRow from "./WarehouseTableRow";
import WarehouseEditableTableRow from "./WarehouseEditableTableRow";

export default function WarehousesPage() {
	const { loading, error, data, refetch } = useQuery(GET_WAREHOUSES);
	const [
		updateWarehouse,
		{
			loading: updateWarehouseLoading,
			error: updateWarehouseError,
			data: updateWarehouseData,
		},
	] = useMutation(UPDATE_WAREHOUSE);

	const [
		deleteWarehouse,
		{
			loading: deleteWarehouseLoading,
			error: deleteWarehouseError,
			data: deleteWarehouseData,
		},
	] = useMutation(DELETE_WAREHOUSE);

	const [addingNewWarehouse, setAddingNewWarehouse] = React.useState(false);
	const [editableRowId, setEditableRowId] = React.useState(null);
	const isEditableRow = (id) => {
		return editableRowId === id;
	};

	const startEditingRow = (id) => {
		if (!id || editableRowId) return;
		setEditableRowId(() => id);
	};
	const cancelEditingRow = (id) => {
		if (id !== editableRowId) return;
		if (id === emptyWarehouse.id) setAddingNewWarehouse(false);
		setEditableRowId(null);
	};
	const saveRow = async (data) => {
		if (editableRowId !== data.id) return;
		await updateWarehouse({
			variables: {
				input: { ...data, id: data.id === emptyWarehouse.id ? "" : data.id },
			},
		});
		await refetch();
		setEditableRowId(null);
		if (data.id === emptyWarehouse.id) {
			setAddingNewWarehouse(false);
		}
	};
	const [deleteRowId, setDeleteRowId] = React.useState(null);
	const tryDeleteRow = (id) => {
		if (deleteRowId) return;
		setDeleteRowId(id);
	};
	const deleteRow = async () => {
		await deleteWarehouse({
			variables: {
				id: deleteRowId,
			},
		});
		setDeleteRowId(null);
		setEditableRowId(null);
		await refetch();
	};

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const isLoading = () =>
		loading || updateWarehouseLoading || deleteWarehouseLoading;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};
	return (
		<>
			{!isLoading() && (
				<div>
					<Typography
						variant="h4"
						align="center"
						component="div"
						href="/"
						sx={{
							fontFamily: "monospace",
							fontWeight: 700,
							margin: "auto",
						}}
					>
						Список складів
					</Typography>
					<div
						style={{
							display: "flex",
							marginRight: 10,
							justifyContent: "flex-end",
						}}
					>
						<div>
							<Button
								onClick={() => {
									setAddingNewWarehouse((oldState) => !oldState);
									setEditableRowId(emptyWarehouse.id);
								}}
								color={addingNewWarehouse ? "secondary" : "primary"}
								variant="contained"
							>
								Створити новий
							</Button>
						</div>
					</div>
					<Paper sx={{ width: "100%", overflow: "hidden" }}>
						<TableContainer sx={{}}>
							<Table stickyHeader aria-label="sticky table">
								<TableHead>
									<TableRow>
										{columns.map((column) => (
											<TableCell
												key={column.id}
												align={column.align}
												style={{ minWidth: column.minWidth }}
											>
												{column.label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{addingNewWarehouse && (
										<WarehouseEditableTableRow
											key={emptyWarehouse.id}
											row={emptyWarehouse}
											actions={{
												saveRow: saveRow,
												cancelEdit: cancelEditingRow,
											}}
										/>
									)}
									{data.warehouses
										.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map((row) => {
											return (
												<>
													{!isEditableRow(row.id) && (
														<WarehouseTableRow
															key={row.id}
															row={row}
															buttonsActive={!editableRowId}
															actions={{
																editRow: startEditingRow,
																tryDeleteRow: tryDeleteRow,
															}}
														/>
													)}
													{isEditableRow(row.id) && (
														<WarehouseEditableTableRow
															key={row.id}
															row={row}
															actions={{
																saveRow: saveRow,
																tryDeleteRow: tryDeleteRow,
																cancelEdit: cancelEditingRow,
															}}
														/>
													)}
												</>
											);
										})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[10, 25, 100]}
							component="div"
							count={data.warehouses.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Paper>
				</div>
			)}
			{isLoading() && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
					}}
				/>
			)}
			<DeleteDialog
				open={!!deleteRowId}
				cancelAction={() => setDeleteRowId(null)}
				confirmAction={deleteRow}
			/>
		</>
	);
}
